import { useEffect, useRef, useState } from 'react';

const conditions = [
  {
    num: '01',
    key: '认知',
    title: 'AI 能理解项目上下文',
    desc: '业务规则、约束、术语、历史决策需要以 AI 可读的方式存在于项目里，而不是分散在每个人的头脑中。',
    icon: 'ri-brain-line',
  },
  {
    num: '02',
    key: '行动',
    title: 'AI 有工具可以行动',
    desc: '编辑器、终端、版本控制、自动化脚本都允许 AI 直接调用，而不是只能给出建议、等人复制粘贴。',
    icon: 'ri-tools-line',
  },
  {
    num: '03',
    key: '检测',
    title: 'AI 的产出能被快速验证',
    desc: '类型检查、Lint、单元测试、接口测试、端到端测试形成快速反馈闭环，AI 自己就能知道刚才做的对不对。',
    icon: 'ri-radar-line',
  },
  {
    num: '04',
    key: '发布',
    title: '验证通过的产出能直接发布',
    desc: '从 AI 修改 → 验证通过 → 合入主干 → 上线的链路是通的，不需要人工把关每一步。',
    icon: 'ri-rocket-2-line',
  },
];

const locks = [
  {
    key: '方法锁死',
    desc: '工程环境没有为 AI 准备好——文档、规范、接口边界、可自动化的检测链路都是缺位的。',
    icon: 'ri-stack-line',
  },
  {
    key: '边界锁死',
    desc: '团队成员被日常手工执行任务占满，没有任何精力去探索 AI 协作的新边界。',
    icon: 'ri-time-line',
  },
  {
    key: '管理锁死',
    desc: '按职能切分的组织架构（前端/后端/测试/运维）人为切断了 AI 本来能打通的全流程。',
    icon: 'ri-organization-chart',
  },
];

export default function ConditionsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {/* Four Conditions */}
      <section id="premise" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className="max-w-3xl mx-auto text-center mb-16 transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
          >
            <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Premise · 先决条件
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              AI 要真正发挥效能，<br className="hidden md:block" />需要四个条件同时成立
            </h2>
            <p
              className="text-base text-[#64748B] leading-relaxed"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              把 AI 工具叠加到传统研发流程上，短期有效，中期必然遇到瓶颈。多数团队卡住的根本原因不是 AI 能力不足，而是认知 / 行动 / 检测 / 发布四个条件里，通常只有"行动"是满的。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {conditions.map((c, idx) => (
              <div
                key={c.num}
                className="bg-[#F8FAFC] rounded-2xl p-7 border border-slate-100 h-full transition-all duration-700"
                style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: `${idx * 90}ms` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#2563EB]/10">
                    <i className={`${c.icon} text-[#2563EB] text-lg`} />
                  </div>
                  <span className="text-xs font-bold text-[#2563EB]/40" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {c.num}
                  </span>
                </div>
                <p className="text-xs uppercase tracking-widest text-[#F97316] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {c.key}
                </p>
                <h3
                  className="text-base font-bold text-[#0F172A] mb-3"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {c.title}
                </h3>
                <p
                  className="text-sm text-[#64748B] leading-relaxed"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Locks */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className="max-w-3xl mx-auto text-center mb-14 transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '200ms' }}
          >
            <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Three Locks · 三重锁死
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              为什么大多数团队<br className="hidden md:block" />靠自己打不破前面的瓶颈
            </h2>
            <p
              className="text-base text-white/55 leading-relaxed"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              方法、边界、管理三道锁同时存在，互相支撑形成恶性循环——再多工具采购也无法从外部打破。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {locks.map((l, idx) => (
              <div
                key={l.key}
                className="bg-white/5 rounded-2xl p-7 border border-white/10 h-full transition-all duration-700"
                style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: `${idx * 100 + 300}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F97316]/20 mb-5">
                  <i className={`${l.icon} text-[#F97316] text-lg`} />
                </div>
                <h3
                  className="text-base font-bold text-white mb-3"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {l.key}
                </h3>
                <p
                  className="text-sm text-white/55 leading-relaxed"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {l.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
